// TOEFL iBT Mock 7 — Writing (2026 format, advanced difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets an
//                       advanced structure (inversion, cleft sentences,
//                       the subjunctive, embedded questions, perfect gerunds).
//                       `accept` lists other word orders that are also
//                       grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, three points that each need reasoning,
//                       formal register expected.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       AND rebut the strongest counterargument.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Has the committee approved the revised budget yet?",
    prefix: "Not yet. Apparently,",
    answer: ["only", "after", "the", "audit", "is", "finished", "will", "they", "vote", "on", "it"],
    accept: [["they", "will", "vote", "on", "it", "only", "after", "the", "audit", "is", "finished"]],
    suffix: ".",
    distractors: ["does", "voting"],
  },
  {
    id: 2,
    speaker: "Why did Dr. Okafor withdraw her article from the journal?",
    prefix: "She discovered that",
    answer: ["the", "survey", "data", "she", "had", "relied", "on", "were", "collected", "improperly"],
    accept: [["the", "survey", "data", "she", "had", "relied", "on", "were", "improperly", "collected"]],
    suffix: ".",
    distractors: ["relies", "whom"],
  },
  {
    id: 3,
    speaker: "What did the dean say about the new attendance policy?",
    prefix: "She wanted to know",
    answer: ["whether", "the", "faculty", "had", "been", "consulted", "before", "it", "was", "announced"],
    suffix: ".",
    distractors: ["have", "announcing"],
  },
  {
    id: 4,
    speaker: "Are you still planning to join the field study in Patagonia?",
    prefix: "I am, provided that",
    answer: ["the", "department", "agrees", "to", "cover", "the", "cost", "of", "my", "equipment"],
    suffix: ".",
    distractors: ["agree", "covering"],
  },
  {
    id: 5,
    speaker: "Why was the chemistry lab closed all of last week?",
    prefix: "It was",
    answer: ["not", "until", "the", "ventilation", "system", "was", "inspected", "that", "we", "were", "allowed", "back", "in"],
    suffix: ".",
    distractors: ["when", "did"],
  },
  {
    id: 6,
    speaker: "Did you end up taking the statistics elective?",
    prefix: "No, and",
    answer: ["I", "regret", "not", "having", "enrolled", "when", "I", "had", "the", "chance"],
    suffix: ".",
    distractors: ["to", "enrolling"],
  },
  {
    id: 7,
    speaker: "What surprised you most about the conference?",
    prefix: "Honestly,",
    answer: ["what", "struck", "me", "was", "how", "few", "presenters", "acknowledged", "their", "limitations"],
    accept: [["how", "few", "presenters", "acknowledged", "their", "limitations", "was", "what", "struck", "me"]],
    suffix: ".",
    distractors: ["striking", "does"],
  },
  {
    id: 8,
    speaker: "Should I submit my thesis proposal this week?",
    prefix: "I would recommend that",
    answer: ["it", "be", "reviewed", "by", "your", "advisor", "before", "you", "submit", "it"],
    suffix: ".",
    distractors: ["is", "will"],
  },
  {
    id: 9,
    speaker: "Why is everyone talking about Professor Reyes's new book?",
    prefix: "Because",
    answer: ["rarely", "has", "a", "historian", "challenged", "such", "a", "widely", "accepted", "theory", "so", "convincingly"],
    accept: [
      ["a", "historian", "has", "rarely", "challenged", "such", "a", "widely", "accepted", "theory", "so", "convincingly"],
      ["rarely", "has", "a", "historian", "so", "convincingly", "challenged", "such", "a", "widely", "accepted", "theory"],
      ["a", "historian", "has", "rarely", "so", "convincingly", "challenged", "such", "a", "widely", "accepted", "theory"],
    ],
    suffix: ".",
    distractors: ["have", "rare"],
  },
  {
    id: 10,
    speaker: "Did the research team finally publish their results?",
    prefix: "They did, but",
    answer: ["not", "before", "several", "of", "their", "key", "findings", "had", "been", "independently", "replicated"],
    accept: [["not", "before", "several", "of", "their", "key", "findings", "had", "been", "replicated", "independently"]],
    suffix: ".",
    distractors: ["has", "replicating"],
  },
];

export const emailTask = {
  recipient: "Professor Halvorsen",
  scenario:
    "You are a teaching assistant for a large introductory economics course. The course coordinator, Professor Halvorsen, has announced that starting next semester, the ten weekly problem sets will be replaced by a single take-home project worth 40% of the final grade. Several students in your discussion section have come to you worried about the change, and you have reservations of your own. Write an email to Professor Halvorsen.",
  points: [
    "Describe the specific concerns students have raised with you about the change.",
    "Explain how the new policy could affect your own work as a teaching assistant.",
    "Propose a compromise that keeps the goals of the new policy while addressing these concerns.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["student", "students", "worried", "concern", "concerns", "anxious", "stress", "stressful", "pressure", "single", "one project", "40", "forty", "percent", "risk", "risky", "feedback", "practice", "weekly", "fall behind", "procrastinate", "procrastination", "deadline", "high-stakes", "high stakes", "struggling", "part-time", "unfair"],
    ["grading", "grade", "workload", "my work", "my role", "office hours", "assistant", "ta", "section", "sections", "mark", "marking", "consistency", "consistent", "rubric", "plagiarism", "time", "hours", "end of the semester", "all at once", "affect me", "difficult for me", "responsible"],
    ["propose", "proposal", "suggest", "suggestion", "recommend", "compromise", "instead", "alternative", "milestone", "milestones", "checkpoint", "checkpoints", "draft", "drafts", "stages", "smaller", "keep some", "reduce", "20", "twenty", "combine", "combination", "both", "balance", "while still", "would allow", "pilot"],
  ],
  formal: true,
  minWords: 110,
  lengthSteps: [[150, 5], [125, 4.5], [105, 4], [85, 3], [65, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "public policy",
  professor: "Dr. Ahmadi",
  question:
    "This unit looks at how governments make decisions under uncertainty. Consider this case: there is evidence, though not yet conclusive, that a widely used pesticide is harming bee populations that pollinate many regional crops. An immediate ban would protect the bees if the evidence is correct, but it would raise costs for farmers and might turn out to be unnecessary. Should governments act on incomplete scientific evidence when the potential harm is serious, or should they wait until the evidence is conclusive? In your post, also identify the strongest argument against your position and explain why it does not change your view.",
  posts: [
    {
      name: "Mei",
      text: "Governments should act early. With pollinators, the damage could be irreversible: once a bee population collapses, it can take decades to recover, and by the time the research is conclusive it may be too late. Farmers' costs are real but temporary, and subsidies could cover the transition.",
    },
    {
      name: "Tomás",
      text: "Acting too early is risky. If governments ban products every time a study raises a concern, they lose credibility and companies stop investing in new products. A better approach is to fund faster research so decisions rest on solid evidence rather than fear.",
    },
  ],
  topicKeywords: [
    "government", "governments", "evidence", "scientific", "science", "research", "study", "studies", "conclusive",
    "uncertain", "uncertainty", "risk", "risks", "harm", "harmful", "ban", "banning", "pesticide", "pesticides",
    "bee", "bees", "pollinator", "pollinators", "farmer", "farmers", "crop", "crops", "cost", "costs",
    "precaution", "precautionary", "regulate", "regulation", "policy", "act", "wait", "irreversible", "damage",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on acting with incomplete evidence",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "act on", "early action", "act before", "restrict", "restriction", "wait for", "certainty", "should act", "should not act", "shouldn't act", "should wait", "should not wait", "act early", "act now", "act immediately", "wait until", "waiting", "precaution", "precautionary", "ban", "banning", "intervene", "regulate", "too early", "too late", "before the evidence", "without waiting"],
    },
    {
      label: "Identify the strongest opposing argument and explain why it is not decisive",
      keywords: ["strongest argument", "strongest objection", "counterargument", "counter-argument", "opposing", "opponents", "critics", "objection", "admittedly", "granted", "it is true that", "one might argue", "some may argue", "some might argue", "some would argue", "others argue", "on the other hand", "nevertheless", "nonetheless", "even so", "this does not", "does not outweigh", "outweigh", "outweighs", "not decisive", "not convincing", "but this", "however"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
